import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from '@umijs/max';
import { Badge, Button, Popover } from 'antd';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import alertAudio from '@/assets/audio/yuy.mp3';
import no_sound from '@/assets/images/no_sound.png';
import sound from '@/assets/images/sound.png';
import { TODO_JUMP_EVENT } from '@/hooks';
import { getTodoSummary, type TodoItem } from '@/services/api/common';
import { useTranslation } from '@/utils/util';

/** 轮询间隔，和 sod 一致 */
const POLL_INTERVAL = 60 * 1000;

/**
 * 待办类型：文案、跳转页面
 * @description 按这里的顺序展示；后端只返回当前账号有菜单权限的项
 */
const TODO_CONFIG: Record<string, { label: string; url: string }> = {
  WITHDRAW_AUDIT: {
    label: 'component.todo.withdrawAudit',
    // 出款审核按「申请中」筛选
    url: '/financeManage/outboundReview?status=0',
  },
  SMS_BALANCE: { label: 'component.todo.smsBalance', url: '/platformManage/smsConfig' },
  ACTIVITY_AUDIT: {
    label: 'component.todo.activityAudit',
    url: '/activityBenefits/activityReview',
  },
};
const TODO_ORDER = Object.keys(TODO_CONFIG);

/**
 * 右上角待处理任务
 * @description 角标是所有待办合计，点开浮窗看明细并跳转处理；有任何待办（total > 0）就播放提示音，可静音
 */
const PendingTasks: React.FC = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<TodoItem[]>([]);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  /** 拉待办汇总，返回合计数；失败返回 undefined */
  const fetchSummary = useCallback(async () => {
    try {
      const res = await getTodoSummary();
      if (res?.code !== '200') return undefined;
      const list = [...(res.data?.items || [])].sort(
        (a, b) => TODO_ORDER.indexOf(a.code) - TODO_ORDER.indexOf(b.code),
      );
      const sum = res.data?.total || 0;
      setItems(list);
      setTotal(sum);
      return sum;
    } catch {
      // 轮询失败不打扰用户，等下一轮
      return undefined;
    }
  }, []);

  useEffect(() => {
    // 只要还有待办（total > 0）就响铃，不分类型；进页面先查一次，之后每轮查完都提醒
    const checkAndRing = async () => {
      const sum = await fetchSummary();
      if (sum && !mutedRef.current) {
        audioRef.current?.play().catch(() => {
          // 浏览器未交互前禁止自动播放，忽略
        });
      }
    };
    checkAndRing();
    const timer = setInterval(checkAndRing, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchSummary]);

  const toggleMute = () => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
  };

  // 账号没有任何待办类型的权限时不展示
  if (!items.length) return null;

  // 三列按各自最长内容定宽、不换行，中英文都能对齐
  const content = (
    <div style={{ position: 'relative', padding: '28px 8px 4px' }}>
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined />}
        onClick={() => setOpen(false)}
        style={{ position: 'absolute', top: 0, right: 0 }}
      />
      {/* 提示音开关放左上角，和关闭按钮同一行 */}
      <span
        onClick={toggleMute}
        style={{
          position: 'absolute',
          top: 4,
          left: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          lineHeight: '20px',
        }}
      >
        <img src={muted ? no_sound : sound} alt="sound" />
        {t(muted ? 'component.todo.soundOff' : 'component.todo.soundOn')}
      </span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content max-content max-content',
          columnGap: 32,
          alignItems: 'center',
          lineHeight: '40px',
          whiteSpace: 'nowrap',
        }}
      >
        {items.map((item) => {
          const config = TODO_CONFIG[item.code];
          return (
            <Fragment key={item.code}>
              <span>{config ? t(config.label) : item.title}</span>
              <span>
                {t('component.todo.pending')}:
                <span style={{ color: '#ff4d4f' }}>{item.count ?? 0}</span>
              </span>
              {config ? (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    setOpen(false);
                    // 带上标记，目标页已缓存时据此重新填筛选条件
                    navigate(config.url, { state: { fromTodo: true } });
                    // 目标页就是当前页签时不会触发切换，靠事件通知它重新查询
                    window.dispatchEvent(new Event(TODO_JUMP_EVENT));
                  }}
                >
                  {t('component.todo.processing')}
                </Button>
              ) : (
                <span />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        placement="bottomRight"
        arrow={false}
        open={open}
        onOpenChange={(visible) => {
          setOpen(visible);
          // 打开时刷新一次，避免看到一分钟前的数
          if (visible) fetchSummary();
        }}
      >
        {/* 顶部操作栏高度固定，角标默认有一半在按钮上方会被裁掉，往下挪进来 */}
        <Badge count={total} overflowCount={99} size="small" offset={[-4, 6]}>
          <Button>{t('component.todo.title')}</Button>
        </Badge>
      </Popover>
      <audio ref={audioRef} preload="auto">
        <source src={alertAudio} type="audio/mpeg" />
      </audio>
    </>
  );
};

export default PendingTasks;
